import { MasterDocument } from '@util/firestore/master';

// Shared by PricingGuideSection.tsx (the /m/ page's full pricing widget) and
// ListingPricingGuideCard.tsx (the compact card in the create-listing flow)
// so both read from one source instead of each declaring its own copy that
// could silently drift. Deliberately kept free of chart.js and any other
// heavy import: PricingGuideSection.tsx pulls in chart.js at module scope
// (`ChartJS.register(...)` is a top-level side effect, so webpack cannot
// tree-shake it out of any bundle that imports the module at all) — before
// this file existed, ListingPricingGuideCard.tsx importing these constants
// FROM PricingGuideSection.tsx dragged chart.js into the create-listing page
// bundle, which previously had none and renders no chart. PR #1727
// adversarial/FRESH EYES review, round 2.

// ─── Data-quality thresholds ───────────────────────────────────────────────
// These constants govern when the pricing guide is shown and how confidently.
// They are deliberately grouped here so Bill can tune them in one place.

// --- Price-section visibility (BOTH must pass, AND) -------------------------
// The price section renders ONLY IF the model has both recent activity AND
// enough trailing-year volume. Either failing => render nothing.
//
//   recent activity:  approved sales within 180 days >= MIN_6MO_RECENT
//   yearly volume:    approved sales within 365 days >= (cinema ? cinema : default)
//
// Rationale: a model with 8 sales last year but none in the last 6 months is
// stale — we hide it rather than show a price the market has moved past.
// Verified 2026-06 the high-volume cinema bodies clear BOTH halves:
//   Sony FX3:  6mo=8 (>=3 OK), 12mo=9 (>=5 OK)
//   Canon C70: 6mo=6 (>=3 OK), 12mo=6 (>=5 OK)
//   Sony FX6:  6mo=4 (>=3 OK), 12mo=7 (>=5 OK)
export const MIN_6MO_RECENT = 3;
export const MIN_12MO_CINEMA = 5;
export const MIN_12MO_DEFAULT = 8;

// Price proxy used ONLY when productType is missing/unknown: cinema bodies are
// expensive, so a high overall median is a reasonable cinema-tier signal.
// Flagged as a heuristic — prefer the explicit productType classification.
const CINEMA_PRICE_PROXY_MEDIAN = 2500;

/**
 * Decide whether this master is a "cinema" body, which gets the lower yearly
 * sample floor. Primary signal: productType === 'cinema_camera' (the canonical
 * GearMaster value — e.g. "Sony FX3 Cinema Camera" master docs carry this
 * type; MasterDocument.productType is the field surfaced on the doc passed to
 * this component). Fallback: when productType is absent, treat a high overall
 * median price as a cinema-tier proxy. Returns { isCinema, method } so the
 * reason is observable.
 */
export function classifyCinema(
  master: MasterDocument,
  overallMedian: number
): { isCinema: boolean; method: 'productType' | 'price_proxy' | 'not_cinema' } {
  const productType = (master as { productType?: string }).productType;
  if (productType) {
    return productType === 'cinema_camera'
      ? { isCinema: true, method: 'productType' }
      : { isCinema: false, method: 'not_cinema' };
  }
  // No productType on the doc — fall back to the price proxy.
  if (overallMedian >= CINEMA_PRICE_PROXY_MEDIAN) {
    return { isCinema: true, method: 'price_proxy' };
  }
  return { isCinema: false, method: 'not_cinema' };
}

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatPrice(n: number): string {
  return fmt.format(n);
}

export type ConditionTab = 'excellent_like_new' | 'good' | 'fair' | 'poor';
